import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtDate, fmtHours, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { PageHeader, LeaveTypeBadge, StatusBadge, BookingDetailDialog, useTableSort, SortableTH, Avatar } from "@/components/common";
import { CoachTip, HelpHint } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X, Pencil, Upload, Download, FileUp, Gift, CalendarPlus, Layers, Trash2 } from "lucide-react";
import { toast } from "sonner";

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const weekdayName = (d) => (d ? WEEKDAY_NAMES[(new Date(d).getDay() + 6) % 7] : "");

export function NewRequestDialog({ onSaved, trigger }) {
  const { user } = useAuth();
  const { types } = useLeaveTypes();
  const activeTypes = types.filter((t) => t.active);
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [preview, setPreview] = useState(null);
  const [f, setF] = useState({
    employee_id: user?.employee_id || "", start_date: "", end_date: "", duration_type: "full",
    hours_value: 0, half_day_start: false, half_day_end: false, leave_type: "annual", employee_notes: "",
    recurring: false, occurrences: 8,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const single = f.start_date && f.start_date === f.end_date;

  useEffect(() => {
    if (open && user?.role !== "employee") api.get("/employees").then((r) => setEmployees(r.data));
  }, [open, user]);

  const doPreview = useCallback(async () => {
    if (!f.employee_id || !f.start_date) { setPreview(null); return; }
    try {
      if (f.recurring) {
        const { data } = await api.post("/requests/recurring/preview", {
          employee_id: f.employee_id, start_date: f.start_date, occurrences: Number(f.occurrences),
          duration_type: f.duration_type, hours_value: Number(f.hours_value), leave_type: f.leave_type });
        setPreview({ recurring: true, ...data });
      } else {
        if (!f.end_date) { setPreview(null); return; }
        const payload = { ...f, hours_value: Number(f.hours_value) };
        const { data } = await api.post("/requests/preview", payload);
        setPreview(data);
      }
    } catch (e) { setPreview(null); }
  }, [f]);

  useEffect(() => { doPreview(); }, [doPreview]);

  const submit = async () => {
    try {
      if (f.recurring) {
        const { data } = await api.post("/requests/recurring", {
          employee_id: f.employee_id, start_date: f.start_date, occurrences: Number(f.occurrences),
          duration_type: f.duration_type, hours_value: Number(f.hours_value), leave_type: f.leave_type,
          employee_notes: f.employee_notes });
        toast.success(`Recurring series created (${data.count} dates, ${fmtHours(data.total_hours)})`);
      } else {
        await api.post("/requests", { ...f, hours_value: Number(f.hours_value) });
        toast.success("Request submitted");
      }
      setOpen(false);
      setF((s) => ({ ...s, start_date: "", end_date: "", employee_notes: "", recurring: false }));
      setPreview(null);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="new-request-btn" className="bg-[#2EA3FD] hover:bg-[#1B8EE6]"><Plus size={16} className="mr-2" />New request</Button>}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New holiday request</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {user?.role !== "employee" && (
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Employee</Label>
              <Select value={f.employee_id} onValueChange={(v) => set("employee_id", v)}>
                <SelectTrigger data-testid="request-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs text-slate-500">{f.recurring ? "First date" : "First date"}</Label><Input data-testid="request-start-input" type="date" value={f.start_date} onChange={(e) => setF((s) => ({ ...s, start_date: e.target.value, end_date: (s.recurring || !s.end_date) ? e.target.value : s.end_date }))} /></div>
          {!f.recurring && <div><Label className="text-xs text-slate-500">Last date</Label><Input data-testid="request-end-input" type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>}
          <div>
            <Label className="text-xs text-slate-500">Leave type</Label>
            <Select value={f.leave_type} onValueChange={(v) => set("leave_type", v)}>
              <SelectTrigger data-testid="request-leavetype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {activeTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {single ? (
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1">Duration <HelpHint text="Full day uses your normal hours for that day. Half day books half. 'Specific hours' lets you enter an exact number — handy for appointments." testid="request-duration-hint" /></Label>
              <Select value={f.duration_type} onValueChange={(v) => set("duration_type", v)}>
                <SelectTrigger data-testid="request-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full day</SelectItem>
                  <SelectItem value="half">Half day</SelectItem>
                  <SelectItem value="hours">Specific hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex items-end gap-4 text-sm">
              <label className="flex items-center gap-2"><Checkbox checked={f.half_day_start} onCheckedChange={(v) => set("half_day_start", !!v)} /> Half first day <HelpHint text="Tick this if you're only off for half of the first day of this range — the first day deducts half your normal hours instead of a full day. Useful when leave starts after a morning at work." testid="request-half-first-hint" /></label>
              <label className="flex items-center gap-2"><Checkbox checked={f.half_day_end} onCheckedChange={(v) => set("half_day_end", !!v)} /> Half last day <HelpHint text="Tick this if you're back for half of the last day — the final day deducts half your normal hours instead of a full day. Useful when you return at lunchtime." testid="request-half-last-hint" /></label>
            </div>
          )}
          {single && f.duration_type === "hours" && (
            <div><Label className="text-xs text-slate-500">Hours</Label><Input data-testid="request-hours-input" type="number" step="0.25" value={f.hours_value} onChange={(e) => set("hours_value", e.target.value)} /></div>
          )}
          <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Notes (optional)</Label><Textarea data-testid="request-notes-input" value={f.employee_notes} onChange={(e) => set("employee_notes", e.target.value)} rows={2} /></div>
          <div className="sm:col-span-2 border-t pt-3">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.recurring} data-testid="request-recurring" onCheckedChange={(v) => setF((s) => ({ ...s, recurring: !!v, end_date: v ? s.start_date : s.end_date }))} /> Repeat weekly on the same weekday</label>
            {f.recurring && (
              <div className="flex items-end gap-3 mt-2">
                <div><Label className="text-xs text-slate-500">Number of weeks</Label><Input type="number" min="1" max="52" value={f.occurrences} onChange={(e) => set("occurrences", e.target.value)} data-testid="request-occurrences" className="w-28" /></div>
                <p className="text-xs text-slate-500 mb-2">Repeats every <strong>{weekdayName(f.start_date) || "—"}</strong>{preview?.count ? ` · ${preview.count} dates` : ""}</p>
              </div>
            )}
          </div>
        </div>

        {preview && (
          <div data-testid="request-preview" className="bg-[#F8FAFC] border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[#0B1A2F]">Calculated deduction</p>
              <p className="text-lg font-bold tabular text-[#2EA3FD]">{fmtHours(preview.total_hours)}{preview.recurring ? ` · ${preview.count} dates` : ` · ${preview.total_days} days`}</p>
            </div>
            {preview.errors?.length > 0 && <p className="text-xs text-rose-600 mb-2">{preview.errors.join("; ")}</p>}
            {preview.conflicts?.length > 0 && (
              <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="request-conflicts">
                <p className="font-semibold mb-1">⚠ {preview.conflicts.length} colleague{preview.conflicts.length > 1 ? "s are" : " is"} also on leave in this period:</p>
                {preview.conflicts.map((c, i) => <div key={i}>· {c.employee_name}{c.department ? ` (${c.department})` : ""}: {fmtDate(c.start_date)} – {fmtDate(c.end_date)} ({c.status})</div>)}
              </div>
            )}
            {preview.capacity?.exceeded && (
              <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-800" data-testid="capacity-warning">
                <p className="font-semibold mb-1">⚠ There are already staff off during this period of leave you are requesting. Your request may not be approved.</p>
                <p className="mb-1">{preview.capacity.count} of a maximum {preview.capacity.limit} already on annual leave:</p>
                {preview.capacity.names.map((c, i) => <div key={i}>· {c.employee_name}: {fmtDate(c.start_date)} – {fmtDate(c.end_date)} ({c.status})</div>)}
              </div>
            )}
            {preview.rows ? (
            <div className="max-h-44 overflow-y-auto cass-scroll">
              <table className="w-full text-xs">
                <thead className="text-slate-500"><tr><th className="text-left py-1">Date</th><th className="text-left">Day</th><th>Week</th><th className="text-right">Scheduled</th><th className="text-right">Deducted</th><th className="text-left pl-2">Note</th></tr></thead>
                <tbody className="tabular">
                  {preview.rows.map((r) => (
                    <tr key={r.date} className={r.is_non_working ? "text-slate-400" : ""}>
                      <td className="py-0.5">{fmtDate(r.date)}</td><td>{r.weekday.slice(0, 3)}</td>
                      <td className="text-center">{r.cycle_week ? `W${r.cycle_week}` : "—"}</td>
                      <td className="text-right">{r.scheduled_hours}h</td>
                      <td className="text-right font-semibold">{r.hours_deducted}h</td>
                      <td className="pl-2 text-slate-500">{r.note}{r.is_bank_holiday ? " 🏦" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            ) : (
            <div className="max-h-44 overflow-y-auto cass-scroll" data-testid="recurring-occurrences">
              {(preview.occurrences || []).map((o) => (
                <div key={o.date} className="flex justify-between text-xs py-0.5 border-b border-slate-100">
                  <span>{fmtDate(o.date)} · {o.weekday}</span>
                  <span className="tabular font-semibold">{o.hours}h{o.note ? ` · ${o.note}` : ""}</span>
                </div>
              ))}
            </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button data-testid="request-submit-btn" onClick={submit} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" disabled={!preview}>Submit request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingsImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const upload = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file"); return; }
    setLoading(true); setResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/bookings/import", fd);
      setResult(data); toast.success(`Imported ${data.created} booking(s)`); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); setFile(null); } }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="import-bookings-btn"><Upload size={16} className="mr-2" />Import bookings</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Import holiday booked / taken</DialogTitle>
          <DialogDescription>Bulk-import existing bookings for the current holiday year.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Columns: <span className="font-mono text-xs">employee_code, leave_type, start_date, end_date, duration_type (full/half/hours), hours_value, status (approved/pending), notes</span>. Dates use <strong>DD/MM/YYYY</strong>. Hours are calculated automatically from each employee's working pattern.</p>
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/bookings/import-template`, "_blank")} data-testid="download-bookings-template-btn"><Download size={15} className="mr-2" />Download CSV template</Button>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            <FileUp size={28} className="mx-auto text-slate-400 mb-2" />
            <Input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="bookings-import-file-input" className="cursor-pointer" />
            {file && <p className="text-xs text-slate-500 mt-2">{file.name}</p>}
          </div>
          {result && (
            <div className="rounded-lg border border-slate-200 p-3 max-h-52 overflow-y-auto cass-scroll" data-testid="bookings-import-result">
              <p className="text-sm font-semibold text-emerald-700">✓ {result.created} booking(s) imported</p>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold text-amber-700">{result.errors.length} row(s) with issues:</p>
                  <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                    {result.errors.map((er, i) => <li key={i}>Row {er.row}{er.code ? ` (${er.code})` : ""}: {er.message}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={upload} disabled={loading} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="bookings-import-upload-btn">{loading ? "Importing…" : "Upload & import"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkAbsenceDialog({ onDone }) {
  const { types } = useLeaveTypes();
  const [open, setOpen] = useState(false);
  const [emps, setEmps] = useState([]);
  const [f, setF] = useState({ leave_type: "other", start_date: "", end_date: "", status: "approved", all_active: true, employee_ids: [], notes: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (open) api.get("/employees").then((r) => setEmps(r.data)).catch(() => {}); }, [open]);
  const toggle = (id) => setF((s) => ({ ...s, employee_ids: s.employee_ids.includes(id) ? s.employee_ids.filter((x) => x !== id) : [...s.employee_ids, id] }));
  const submit = async () => {
    if (!f.start_date || !f.end_date) { toast.error("Choose start and end dates"); return; }
    try {
      const { data } = await api.post("/requests/bulk", { ...f, duration_type: "full" });
      toast.success(`Created ${data.created} bookings (${fmtHours(data.total_hours)})`);
      setOpen(false); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="bulk-absence-btn"><CalendarPlus size={16} className="mr-2" />Bulk absence</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add bulk absence</DialogTitle>
          <DialogDescription>Book the same dates (e.g. Christmas shutdown) for all or selected staff.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Leave type</Label>
            <Select value={f.leave_type} onValueChange={(v) => set("leave_type", v)}>
              <SelectTrigger data-testid="bulk-leavetype-select"><SelectValue /></SelectTrigger>
              <SelectContent>{types.filter((t) => t.active).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">From</Label><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="bulk-start-input" /></div>
            <div><Label className="text-xs text-slate-500">To</Label><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="bulk-end-input" /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="approved">Approved</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.all_active} onCheckedChange={(v) => set("all_active", !!v)} data-testid="bulk-all-active" /> Apply to all active staff</label>
          {!f.all_active && (
            <div className="border rounded-lg p-2 max-h-48 overflow-y-auto cass-scroll space-y-1" data-testid="bulk-employee-list">
              {emps.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm py-0.5">
                  <Checkbox checked={f.employee_ids.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`bulk-emp-${e.employee_code}`} />
                  {e.first_name} {e.last_name} <span className="text-xs text-slate-400">({e.employee_code})</span>
                </label>
              ))}
            </div>
          )}
          <div><Label className="text-xs text-slate-500">Notes (optional)</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          <p className="text-xs text-slate-400">Hours are calculated per employee from their working pattern.</p>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="bulk-submit-btn">Create bookings</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageBulkDialog({ onDone }) {
  const { types } = useLeaveTypes();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/requests/bulk-groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const startEdit = (g) => setEditing({ group: g.group, start_date: g.start_date, end_date: g.end_date, leave_type: g.leave_type });
  const saveEdit = async () => {
    try {
      await api.put(`/requests/bulk/${editing.group}`, { start_date: editing.start_date, end_date: editing.end_date, leave_type: editing.leave_type });
      toast.success("Bulk absence updated"); setEditing(null); load(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete this bulk absence for all ${g.count} staff?`)) return;
    try { await api.delete(`/requests/bulk/${g.group}`); toast.success("Bulk absence deleted"); load(); onDone && onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeMember = async (g, m) => {
    if (!window.confirm(`Remove ${m.employee_name} from this bulk absence?`)) return;
    try { await api.delete(`/requests/bulk/${g.group}/booking/${m.booking_id}`); toast.success("Removed"); load(); onDone && onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="manage-bulk-btn"><Layers size={16} className="mr-2" />Manage bulk</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Manage bulk absences</DialogTitle>
          <DialogDescription>Edit dates or leave type, remove individual staff, or delete the whole bulk booking.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {groups.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No bulk absences created yet.</p>}
          {groups.map((g) => (
            <div key={g.group} className="border border-slate-200 rounded-lg p-3" data-testid={`bulk-group-${g.group}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2"><LeaveTypeBadge type={g.leave_type} /><StatusBadge status={g.status === "mixed" ? "pending" : g.status} /></div>
                  <p className="text-sm font-medium text-[#0B1A2F]">{fmtDate(g.start_date)} – {fmtDate(g.end_date)}</p>
                  <p className="text-xs text-slate-500">{g.count} staff · {fmtHours(g.total_hours)} total</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#2EA3FD] h-7" data-testid={`edit-bulk-${g.group}`} onClick={() => startEdit(g)}><Pencil size={14} className="mr-1" />Edit</Button>
                  <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 h-7" data-testid={`delete-bulk-${g.group}`} onClick={() => deleteGroup(g)}><Trash2 size={14} className="mr-1" />Delete</Button>
                </div>
              </div>
              {editing?.group === g.group && (
                <div className="mt-3 bg-[#F8FAFC] border rounded-lg p-3 grid sm:grid-cols-3 gap-3 items-end">
                  <div><Label className="text-xs text-slate-500">From</Label><Input type="date" value={editing.start_date} onChange={(e) => setEditing((s) => ({ ...s, start_date: e.target.value }))} data-testid="edit-bulk-start" /></div>
                  <div><Label className="text-xs text-slate-500">To</Label><Input type="date" value={editing.end_date} onChange={(e) => setEditing((s) => ({ ...s, end_date: e.target.value }))} data-testid="edit-bulk-end" /></div>
                  <div><Label className="text-xs text-slate-500">Leave type</Label>
                    <Select value={editing.leave_type} onValueChange={(v) => setEditing((s) => ({ ...s, leave_type: v }))}>
                      <SelectTrigger data-testid="edit-bulk-leavetype"><SelectValue /></SelectTrigger>
                      <SelectContent>{types.filter((t) => t.active).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-3 flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                    <Button size="sm" className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={saveEdit} data-testid="save-bulk-edit">Save changes</Button>
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {g.members.map((m) => (
                  <span key={m.booking_id} className="inline-flex items-center gap-1 bg-slate-100 rounded-full pl-2.5 pr-1 py-0.5 text-xs text-slate-700" data-testid={`bulk-member-${m.booking_id}`}>
                    {m.employee_name}
                    <button onClick={() => removeMember(g, m)} className="rounded-full hover:bg-rose-100 hover:text-rose-600 p-0.5" data-testid={`remove-bulk-member-${m.booking_id}`}><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditRequestDialog({ booking, onSaved, onClose }) {
  const { types } = useLeaveTypes();
  const activeTypes = types.filter((t) => t.active);
  const [f, setF] = useState({
    start_date: booking.start_date, end_date: booking.end_date, duration_type: booking.duration_type || "full",
    hours_value: booking.hours_value || 0, half_day_start: booking.half_day_start || false,
    half_day_end: booking.half_day_end || false, leave_type: booking.leave_type || "annual",
  });
  const [preview, setPreview] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const single = f.start_date && f.start_date === f.end_date;
  useEffect(() => {
    if (!f.start_date || !f.end_date) { setPreview(null); return; }
    api.post("/requests/preview", { employee_id: booking.employee_id, ...f, hours_value: Number(f.hours_value) })
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [f, booking.employee_id]);
  const save = async () => {
    try {
      await api.put(`/requests/${booking.id}`, { ...f, hours_value: Number(f.hours_value) });
      toast.success("Request updated"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-request-dialog">
        <DialogHeader><DialogTitle>Edit request — {booking.reference}</DialogTitle>
          <DialogDescription>{booking.employee_name} · {booking.status}</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs text-slate-500">First date</Label><Input type="date" value={f.start_date} onChange={(e) => setF((s) => ({ ...s, start_date: e.target.value, end_date: s.end_date && s.end_date >= e.target.value ? s.end_date : e.target.value }))} data-testid="edit-req-start" /></div>
          <div><Label className="text-xs text-slate-500">Last date</Label><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="edit-req-end" /></div>
          <div><Label className="text-xs text-slate-500">Leave type</Label>
            <Select value={f.leave_type} onValueChange={(v) => set("leave_type", v)}>
              <SelectTrigger data-testid="edit-req-leavetype"><SelectValue /></SelectTrigger>
              <SelectContent>{activeTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-500">Duration</Label>
            <Select value={f.duration_type} onValueChange={(v) => set("duration_type", v)}>
              <SelectTrigger data-testid="edit-req-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full day(s)</SelectItem>
                <SelectItem value="half">Half day</SelectItem>
                <SelectItem value="hours">Specific hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.duration_type === "hours" && <div><Label className="text-xs text-slate-500">Hours per day</Label><Input type="number" step="0.5" value={f.hours_value} onChange={(e) => set("hours_value", e.target.value)} data-testid="edit-req-hours" /></div>}
          {f.duration_type === "half" && single && (
            <div className="flex items-center gap-4 pt-6">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.half_day_start} onCheckedChange={(v) => set("half_day_start", !!v)} data-testid="edit-req-half-start" />Half day (AM/start)</label>
            </div>
          )}
        </div>
        {preview && (
          <div className="rounded-lg bg-[#F8FAFC] border p-3 text-sm">
            <p className="font-medium text-[#0B1A2F]">Recalculated: {fmtHours(preview.total_hours)} · {preview.total_days} day(s)</p>
            {preview.errors?.length > 0 && <p className="text-rose-600 text-xs mt-1">{preview.errors.join("; ")}</p>}
          </div>
        )}
        <DialogFooter><Button onClick={save} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="save-edit-request" disabled={preview?.errors?.length > 0}>Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeLeaveBanner({ empId, yearId }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/employees/${empId}/summary`, { params: yearId ? { year_id: yearId } : {} }).then((r) => setS(r.data)).catch(() => setS(null)); }, [empId, yearId]);
  if (!s) return null;
  const ent = s.total_entitlement || 0;
  const booked = s.total_hours_booked || 0;
  const avail = s.confirmed_remaining || 0;
  const pct = ent > 0 ? Math.min(100, Math.max(0, Math.round((booked / ent) * 100))) : 0;
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1A2F] via-[#0F2C50] to-[#2EA3FD] text-white p-6 sm:p-7" data-testid="leave-summary-banner">
      <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10" />
      <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#8FD0FF]">Leave &amp; Absence{s.holiday_year?.name ? ` · ${s.holiday_year.name}` : ""}</p>
          <h2 className="text-2xl font-extrabold font-display mt-1">Your leave at a glance</h2>
          <div className="flex gap-8 mt-4">
            <div><p className="text-[11px] text-[#B9DCFF]">Allowance</p><p className="text-2xl font-bold" data-testid="leave-banner-allowance">{fmtHours(ent)}</p></div>
            <div><p className="text-[11px] text-[#B9DCFF]">Taken / booked</p><p className="text-2xl font-bold" data-testid="leave-banner-booked">{fmtHours(booked)}</p></div>
            <div><p className="text-[11px] text-[#B9DCFF]">Available</p><p className="text-2xl font-bold text-white" data-testid="leave-banner-available">{fmtHours(avail)}</p></div>
          </div>
        </div>
        <div className="w-full sm:w-72">
          <div className="h-2.5 rounded-full bg-white/20 overflow-hidden"><div className="h-full bg-white transition-all" style={{ width: `${pct}%` }} data-testid="leave-banner-bar" /></div>
          <p className="text-xs text-[#B9DCFF] mt-2">{pct}% of your allowance is booked or taken.</p>
        </div>
      </div>
    </div>
  );
}

export default function Requests() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const focusId = sp.get("focus");
  const canManage = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [years, setYears] = useState([]);
  const [yearFilter, setYearFilter] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [detailBooking, setDetailBooking] = useState(null);
  const [editReq, setEditReq] = useState(null);
  const [empFilter, setEmpFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [employees, setEmployees] = useState([]);
  const { types } = useLeaveTypes();
  const sortAcc = {
    employee: (b) => b.employee_name, type: (b) => b.leave_type,
    start_date: (b) => b.start_date, hours: (b) => b.total_hours, days: (b) => b.total_days, status: (b) => b.status,
  };
  const yObj = years.find((y) => y.id === yearFilter);
  const inYear = (b) => yearFilter === "all" || !yObj || b.holiday_year_id === yObj.id || (b.start_date >= yObj.start_date && b.start_date <= yObj.end_date);
  const visible = rows.filter((b) => (empFilter === "all" || b.employee_id === empFilter) && (typeFilter === "all" || b.leave_type === typeFilter) && inYear(b));
  const { sorted, sort, toggle } = useTableSort(visible, sortAcc, { key: "start_date", dir: "desc" });
  const load = useCallback(() => {
    const params = status === "all" ? {} : { status };
    api.get("/requests", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.role !== "employee") api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { api.get("/holiday-years").then((r) => setYears(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (focusId && rows.length) {
      const b = rows.find((x) => x.id === focusId || x.recurrence_group === focusId);
      if (b) setDetailBooking(b);
    }
  }, [focusId, rows]);

  const cancel = async (b) => {
    try {
      if (b.recurrence_group) await api.post(`/requests/group/${b.recurrence_group}/cancel`, { cancellation_reason: "Cancelled by user" });
      else await api.post(`/requests/${b.id}/cancel`, { cancellation_reason: "Cancelled by user" });
      toast.success("Cancelled"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteReq = async (b) => {
    if (!window.confirm(`Delete request ${b.reference}? ${b.status === "approved" ? `This returns ${fmtHours(b.total_hours)} to the balance.` : ""}`)) return;
    try { await api.delete(`/requests/${b.id}`); toast.success("Request deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveNotes = async () => {
    try { await api.put(`/requests/${editRow.id}/notes`, { employee_notes: noteText }); toast.success("Notes updated"); setEditRow(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const generateBirthdays = async () => {
    try {
      const { data } = await api.post("/birthdays/generate");
      toast.success(`Birthday leave: ${data.created} added · ${data.skipped} already there · ${data.non_working_skipped} on non-working days`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <CoachTip tipKey="requests" title="Booking leave is quick">Click New request, pick your dates and leave type — we work out the exact deduction, including part-days.</CoachTip>
      {user?.role === "employee" && user?.employee_id && <EmployeeLeaveBanner empId={user.employee_id} yearId={yearFilter !== "all" ? yearFilter : undefined} />}
      <PageHeader title="Leave & Absence" subtitle="Book time off and track your balance">
        {user?.role === "admin" && <Button variant="outline" onClick={generateBirthdays} data-testid="generate-birthdays-btn"><Gift size={16} className="mr-2" />Birthday leave</Button>}
        {user?.role === "admin" && <BulkAbsenceDialog onDone={load} />}
        {user?.role === "admin" && <ManageBulkDialog onDone={load} />}
        {user?.role === "admin" && <BookingsImportDialog onDone={load} />}
        <NewRequestDialog onSaved={load} />
      </PageHeader>
      <div className="flex flex-wrap gap-2 items-center">
        {["all", "pending", "approved", "declined", "cancelled"].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-full border capitalize ${status === s ? "bg-[#0B1A2F] text-white border-[#0B1A2F]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{s}</button>
        ))}
        <div className="w-px h-6 bg-slate-200 mx-1" />
        {user?.role !== "employee" && (
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger className="h-8 w-52 text-xs bg-white" data-testid="filter-employee"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-44 text-xs bg-white" data-testid="filter-leavetype"><SelectValue placeholder="All leave types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All leave types</SelectItem>
            {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {years.length > 1 && (
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="h-8 w-44 text-xs bg-white" data-testid="filter-holiday-year"><SelectValue placeholder="All holiday years" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All holiday years</SelectItem>
              {years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(empFilter !== "all" || typeFilter !== "all" || yearFilter !== "all") && (
          <button onClick={() => { setEmpFilter("all"); setTypeFilter("all"); setYearFilter("all"); }} className="text-xs text-slate-500 hover:text-[#2EA3FD] underline" data-testid="clear-filters">Clear</button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto cass-scroll">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <SortableTH label="Employee" sortId="employee" sort={sort} onSort={toggle} />
            <SortableTH label="Type" sortId="type" sort={sort} onSort={toggle} />
            <SortableTH label="Dates" sortId="start_date" sort={sort} onSort={toggle} />
            <SortableTH label="Hours" sortId="hours" sort={sort} onSort={toggle} />
            <SortableTH label="Days" sortId="days" sort={sort} onSort={toggle} />
            <SortableTH label="Status" sortId="status" sort={sort} onSort={toggle} />
            <TableHead className="text-xs uppercase tracking-wider text-slate-600"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sorted.map((b) => (
              <TableRow key={b.id} data-testid={`request-row-${b.reference}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailBooking(b)}>
                <TableCell className="font-medium"><span className="inline-flex items-center gap-2.5"><Avatar name={b.employee_name} photo={b.employee_photo} size={28} /><span>{b.employee_name}{b.recurrence_group && <span className="ml-1 rounded bg-violet-100 text-violet-700 px-1 text-[9px]" data-testid="series-tag">series</span>}</span></span></TableCell>
                <TableCell><LeaveTypeBadge type={b.leave_type} /></TableCell>
                <TableCell className="text-sm">{fmtDate(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtDate(b.end_date)}` : ""}</TableCell>
                <TableCell className="tabular">{fmtHours(b.total_hours)}</TableCell>
                <TableCell className="tabular">{b.total_days}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {canManage && ["pending", "approved"].includes(b.status) && (
                      <Button variant="ghost" size="sm" data-testid={`edit-request-${b.reference}`} onClick={() => setEditReq(b)} className="text-slate-500 hover:text-[#2EA3FD] h-7"><Pencil size={13} className="mr-1" />Edit</Button>
                    )}
                    {["pending", "approved"].includes(b.status) && (
                      <Button variant="ghost" size="sm" data-testid={`edit-notes-${b.reference}`} onClick={() => { setEditRow(b); setNoteText(b.employee_notes || ""); }} className="text-slate-500 hover:text-[#2EA3FD] h-7"><Pencil size={13} className="mr-1" />Notes</Button>
                    )}
                    {b.status === "pending" && (
                      <Button variant="ghost" size="sm" data-testid={`cancel-request-${b.reference}`} onClick={() => cancel(b)} className="text-amber-600 hover:text-amber-700 h-7"><X size={14} className="mr-1" />Cancel{b.recurrence_group ? " series" : ""}</Button>
                    )}
                    {canManage && ["pending", "approved"].includes(b.status) && (
                      <Button variant="ghost" size="sm" data-testid={`delete-request-${b.reference}`} onClick={() => deleteReq(b)} className="text-rose-600 hover:text-rose-700 h-7"><Trash2 size={14} className="mr-1" />Delete</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No requests</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit notes — {editRow?.reference}</DialogTitle></DialogHeader>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4} data-testid="edit-notes-input" placeholder="Your notes for this request…" />
          <DialogFooter><Button onClick={saveNotes} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="save-notes-btn">Save notes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BookingDetailDialog booking={detailBooking} open={!!detailBooking} onOpenChange={(o) => !o && setDetailBooking(null)} />
      {editReq && <EditRequestDialog booking={editReq} onSaved={load} onClose={() => setEditReq(null)} />}
    </div>
  );
}
